import { useQuery } from '@tanstack/react-query';
// import { ExternalLink } from 'lucide-react';
import { Badge, statusTone } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Step } from '@/components/runs/Step';
import {
  RUN_DETAIL_IDLE_POLL_MS,
  RUN_DETAIL_RUNNING_POLL_MS,
  // TEMPORAL_NAMESPACE,
  // TEMPORAL_UI_BASE,
} from '@/constants/runs';
import { api } from '@/lib/api';
import { formatDuration, formatRelative } from '@/lib/format';

export function RunDetailPage({ workflowId }: { workflowId: string }) {
  const detailQ = useQuery({
    queryKey: ['run', workflowId, 'detail'],
    queryFn: () => api.getWorkflow(workflowId),
    refetchInterval: (q) => {
      const data = q.state.data;
      return data?.status === 'RUNNING' ? RUN_DETAIL_RUNNING_POLL_MS : RUN_DETAIL_IDLE_POLL_MS;
    },
  });
  const eventsQ = useQuery({
    queryKey: ['run', workflowId, 'events'],
    queryFn: () => api.getWorkflowEvents(workflowId),
    refetchInterval: (q) => {
      const data = q.state.data;
      const running = data?.steps?.some((s) => s.status === 'running');
      return running ? RUN_DETAIL_RUNNING_POLL_MS : RUN_DETAIL_IDLE_POLL_MS;
    },
  });

  const detail = detailQ.data;
  const events = eventsQ.data;
  // const temporalLink = detail
  //   ? `${TEMPORAL_UI_BASE}/namespaces/${TEMPORAL_NAMESPACE}/workflows/${encodeURIComponent(detail.workflow_id)}/${encodeURIComponent(detail.run_id)}/history`
  //   : null;

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">Run</h1>
          <p className="font-mono text-xs text-muted-foreground">{workflowId}</p>
          {detail && (
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone={statusTone(detail.status)}>{detail.status}</Badge>
              <Badge tone="neutral">{detail.workflow_type}</Badge>
              <span className="text-xs text-muted-foreground">
                started {formatRelative(detail.start_time)}
              </span>
              <span className="text-xs text-muted-foreground">
                duration {formatDuration(detail.start_time, detail.close_time)}
              </span>
              <span className="text-xs text-muted-foreground">
                {detail.history_length} events
              </span>
            </div>
          )}
        </div>
        {/* {temporalLink && (
          <a
            href={temporalLink}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium hover:bg-muted"
          >
            <ExternalLink className="h-3.5 w-3.5" /> Open in Temporal UI
          </a>
        )} */}
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Activity timeline</CardTitle>
        </CardHeader>
        <CardContent>
          {eventsQ.isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-14" />
              <Skeleton className="h-14" />
              <Skeleton className="h-14" />
            </div>
          ) : eventsQ.isError ? (
            <p className="text-sm text-destructive">
              Failed to load events: {String(eventsQ.error)}
            </p>
          ) : (events?.steps ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No activities scheduled yet. (Workflow may have started in the last second.)
            </p>
          ) : (
            <ol className="space-y-2">
              {events!.steps.map((step, i) => (
                <Step key={`${step.activity_id}-${i}`} step={step} index={i + 1} />
              ))}
            </ol>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
